import { Chakra } from "@airhelp/react-ui-components";
import BreadcrumbsSchema from "modules/storyblok/components/BreadcrumbsSchema";
import { stickyBottomBarFirstTriggerStyles } from "components/StickyBottomBar";
import StickyBottomBarTrigger from "components/StickyBottomBar/StickyBottomBarTrigger";
import { DataContextProvider } from "contexts/DataContext";
import { useLanguage } from "contexts/LocalizationContext";
import { MobileMenuContextProvider } from "contexts/MobileMenuContext";
import { ModalContextProvider } from "contexts/ModalContext";
import { StickyBottomBarContextProvider } from "contexts/StickyBottomBarContext";
import CustomHead from "modules/common/components/CustomHead";
import Footer from "modules/common/components/Footer";
import Grid from "modules/common/components/Grid";
import HeaderNav from "modules/common/components/HeaderNav";
import CheckCompensationButton from "modules/common/components/HeaderNav/CheckCompensationButton";
import MenuItems from "modules/common/components/HeaderNav/MenuItems";
import PressLogos from "modules/common/components/PressLogos";
import { DynamicComponent } from "modules/storyblok/DynamicComponent";
import PropTypes from "prop-types";
import { useTopMenu } from "utils/menuUtils";
import StickyBottomBarAdapter from "../adapters/StickyBottomBar";

const { Box } = Chakra;

const BasicTemplate = ({ story, cmsLanguagesData, serverSideStoryblokPages, whiteListDomains }) => {
  const {
    tag_list: tagList,
    content: {
      metaTitle,
      metaDescription,
      body,
      breadcrumbsParentPage,
      breadcrumbsPositionName,
      stickyBottomBarHeading,
      hideStickyBottomBar,
      stickyBottomBarVariant,
    },
  } = story;

  const language = useLanguage();
  const topMenu = useTopMenu(language);
  const noindex = tagList.includes("noindex");

  return (
    <DataContextProvider data={{ cmsLanguagesData, serverSideStoryblokPages, whiteListDomains }}>
      <CustomHead title={metaTitle} description={metaDescription} noindex={noindex} />
      <BreadcrumbsSchema
        breadcrumbsPositionName={breadcrumbsPositionName}
        breadcrumbsParentPage={breadcrumbsParentPage}
      />
      <MobileMenuContextProvider>
        <ModalContextProvider>
          <HeaderNav menuItems={<MenuItems menu={topMenu} />} headerButtons={<CheckCompensationButton />} />
          <StickyBottomBarTrigger type="first" styles={stickyBottomBarFirstTriggerStyles} />
          <Grid pt={32} as="main">
            <Box gridColumn={{ base: "initial", md: "2 / span 10", lg: "3 / span 8" }}>
              {body ? body.map((blok) => <DynamicComponent blok={blok} key={blok._uid} />) : null}
            </Box>
          </Grid>
          <PressLogos />
          <Footer />
        </ModalContextProvider>
      </MobileMenuContextProvider>
      <StickyBottomBarAdapter
        showStickyBottomBar={!hideStickyBottomBar}
        stickyBottomBarVariant={stickyBottomBarVariant}
        stickyBottomBarHeading={stickyBottomBarHeading}
      />
    </DataContextProvider>
  );
};

const BasicTemplateAdapter = (props) => (
  <StickyBottomBarContextProvider threshold={0.4}>
    <BasicTemplate {...props} />
  </StickyBottomBarContextProvider>
);

export default BasicTemplateAdapter;

BasicTemplate.propTypes = {
  story: PropTypes.shape({
    tag_list: PropTypes.arrayOf(PropTypes.string),
    content: PropTypes.shape({
      body: PropTypes.arrayOf(PropTypes.shape({})),
      metaTitle: PropTypes.string,
      metaDescription: PropTypes.string,
      breadcrumbsParentPage: PropTypes.shape({}),
      breadcrumbsPositionName: PropTypes.string,
    }),
  }).isRequired,
  cmsLanguagesData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      slug: PropTypes.string,
      full_slug: PropTypes.string,
      is_folder: PropTypes.bool,
      parent_id: PropTypes.number,
    })
  ),
  whiteListDomains: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string,
      dimension_value: PropTypes.string,
    })
  ),
  serverSideStoryblokPages: PropTypes.shape({}).isRequired,
};

BasicTemplate.defaultProps = {
  cmsLanguagesData: [],
  whiteListDomains: [],
};
